import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { servicesData, serviceBundlesData } from "@/data/mockData";
import { pharmaciesData, totalServicePrice } from "@/data/platformData";
import { Package, Activity, Building2, FileSignature, Check, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { MDRBadge } from "@/components/MDRBadge";

export const BookingServiceStep = ({ booking, updateBooking, initiator, isCitizen }) => {
  const [tab, setTab] = useState("bundles");

  const togglePharmacy = (id) => updateBooking({ pharmacyId: id });

  const toggleService = (sid) => {
    const exists = booking.serviceIds.includes(sid);
    const next = exists
      ? booking.serviceIds.filter(x => x !== sid)
      : [...booking.serviceIds, sid];
    updateBooking({ serviceIds: next, bundleId: null });
  };

  const selectBundle = (bundle) => {
    updateBooking({ bundleId: bundle.id, serviceIds: [...bundle.services] });
  };

  const total = totalServicePrice(booking.serviceIds);
  const requiresReport = booking.serviceIds.some(sid => servicesData.find(s => s.id === sid)?.requiresReport);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold">Prestazione e Farmacia</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {isCitizen
            ? "Scegli la farmacia e le prestazioni che desideri prenotare."
            : "Seleziona profilo o singole prestazioni associate alla farmacia."}
        </p>
      </div>

      {/* Pharmacy selector (always shown for citizen, optional for operator if multi-pharmacy) */}
      <div className="space-y-2">
        <p className="text-sm font-medium flex items-center gap-2">
          <MapPin className="h-4 w-4 text-primary" /> Farmacia
        </p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {pharmaciesData.map(p => (
            <Card
              key={p.id}
              onClick={() => togglePharmacy(p.id)}
              data-testid={`booking-pharmacy-${p.id}`}
              className={cn(
                "p-3 cursor-pointer transition-all",
                booking.pharmacyId === p.id
                  ? "border-2 border-primary shadow-md bg-primary/5"
                  : "border hover:border-primary/40"
              )}
            >
              <div className="flex items-start gap-2">
                <Building2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="font-medium text-sm leading-tight truncate">{p.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{p.address}</p>
                </div>
                {booking.pharmacyId === p.id && <Check className="h-4 w-4 text-primary ml-auto shrink-0" />}
              </div>
            </Card>
          ))}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="bundles" data-testid="booking-tab-bundles">
            <Package className="h-4 w-4 mr-2" /> Profili
          </TabsTrigger>
          <TabsTrigger value="single" data-testid="booking-tab-single">
            <Activity className="h-4 w-4 mr-2" /> Singole prestazioni
          </TabsTrigger>
        </TabsList>

        <TabsContent value="bundles" className="mt-5">
          <div className="grid gap-3 sm:grid-cols-2">
            {serviceBundlesData.map(b => {
              const selected = booking.bundleId === b.id;
              return (
                <Card
                  key={b.id}
                  onClick={() => selectBundle(b)}
                  data-testid={`booking-bundle-${b.id}`}
                  className={cn(
                    "p-4 cursor-pointer transition-all",
                    selected ? "border-2 border-primary shadow-md bg-primary/5" : "border hover:border-primary/40"
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold">{b.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{b.description}</p>
                    </div>
                    {b.requiresReport && (
                      <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200 shrink-0">
                        <FileSignature className="h-2.5 w-2.5 mr-1" /> Refertazione
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-3">
                    {b.services.map(sid => {
                      const s = servicesData.find(x => x.id === sid);
                      return (
                        <Badge key={sid} variant="secondary" className="text-[10px]">
                          {s?.name}
                        </Badge>
                      );
                    })}
                  </div>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="single" className="mt-5">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {servicesData.map(s => {
              const selected = booking.serviceIds.includes(s.id);
              return (
                <Card
                  key={s.id}
                  onClick={() => toggleService(s.id)}
                  data-testid={`booking-service-${s.id}`}
                  className={cn(
                    "p-3 cursor-pointer transition-all",
                    selected ? "border-2 border-primary shadow-md bg-primary/5" : "border hover:border-primary/40"
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <p className="font-medium text-sm leading-tight">{s.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{s.duration} min · {s.category}</p>
                    </div>
                    {selected && <Check className="h-4 w-4 text-primary shrink-0" />}
                  </div>
                  {s.requiresReport && (
                    <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200 mt-2">
                      <FileSignature className="h-2.5 w-2.5 mr-1" /> Refertazione
                    </Badge>
                  )}
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Summary */}
      {booking.serviceIds.length > 0 && (
        <Card className="p-4 bg-muted/40 border-dashed">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <span className="font-medium">{booking.serviceIds.length}</span> prestazioni selezionate
              {booking.bundleId && <span className="text-muted-foreground"> (profilo {serviceBundlesData.find(x => x.id === booking.bundleId)?.name})</span>}
            </div>
            <div className="flex items-center gap-3">
              {requiresReport && <MDRBadge variant="medical" size="sm" />}
              <span className="text-base font-bold text-primary">€{total.toFixed(2)}</span>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default BookingServiceStep;
